import type { ChartOption, DashboardChart } from "./types";

const DESIGN_NUM_FONT = "'Barlow','Noto Sans JP',ui-sans-serif,sans-serif";

/**
 * The pipeline now emits ECharts options directly in the "Market Signals"
 * design tokens (accent teal #00ADB5, ink #26282A, negative #B5544B, dc grid
 * lines, Barlow/Noto Sans JP typography) — see pipeline/generate.py. This
 * helper's job has shrunk to injecting a font-family fallback onto textStyle
 * blocks that already exist, so the frontend still has exactly one place
 * that guarantees the Barlow/Noto Sans JP stack is applied, without
 * duplicating color/theme knowledge that now lives solely in the pipeline.
 */
export function applyDesignTheme(option: ChartOption, _chartType: DashboardChart["type"]): ChartOption {
  const themed = cloneChartOption(option);

  applyDesignFont(themed);

  if (isRecord(themed.tooltip) && isRecord(themed.tooltip.textStyle)) {
    themed.tooltip = { ...themed.tooltip, textStyle: { ...themed.tooltip.textStyle, fontFamily: DESIGN_NUM_FONT } };
  }

  if (isRecord(themed.legend) && isRecord(themed.legend.textStyle)) {
    themed.legend = {
      ...themed.legend,
      textStyle: { ...themed.legend.textStyle, fontFamily: DESIGN_NUM_FONT },
    };
  }

  return themed;
}

function applyDesignFont(node: Record<string, unknown>): void {
  // Only touch textStyle where it already exists, to avoid introducing
  // unexpected fields on option nodes that never had typography config.
  if (isRecord(node.textStyle)) {
    node.textStyle = { ...node.textStyle, fontFamily: DESIGN_NUM_FONT };
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cloneChartOption<T>(option: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(option);
  }

  return JSON.parse(JSON.stringify(option)) as T;
}

function getSeriesList(option: Record<string, unknown>): Record<string, unknown>[] {
  const series = option.series;

  if (Array.isArray(series)) {
    return series.filter(isRecord);
  }

  return isRecord(series) ? [series] : [];
}

type CompositionNode = Record<string, unknown> & {
  name?: string;
  value?: number;
  children?: CompositionNode[];
};

function getCompositionChildren(node: CompositionNode): CompositionNode[] {
  return Array.isArray(node.children) ? node.children.filter(isRecord) : [];
}

function findCompositionNode(nodes: CompositionNode[], path: string[]): CompositionNode | null {
  if (!path.length) {
    return null;
  }

  const [currentName, ...restPath] = path;
  const currentNode = nodes.find((node) => node.name === currentName);

  if (!currentNode) {
    return null;
  }

  return restPath.length ? findCompositionNode(getCompositionChildren(currentNode), restPath) : currentNode;
}

export interface CompositionBarSegment {
  name: string;
  value: number;
  share: number;
  color: string;
  hasChildren: boolean;
  fullPath: string[];
}

export interface CompositionBarView {
  /** Segments in the current level, sorted by value descending. */
  segments: CompositionBarSegment[];
  /** ECharts option for the single 100%-stacked horizontal bar. */
  option: Record<string, unknown>;
}

const DEFAULT_COMPOSITION_PALETTE = [
  "#00ADB5",
  "#3E8E93",
  "#6FA9AC",
  "#94BEC0",
  "#B4D2D3",
  "#CFDFDF",
  "#E7E5DC",
];

/**
 * Flattens the pipeline's name/value/children hierarchy tree (still one tree,
 * same shape the old sunburst consumed) into the dc-style single-row 100%
 * stacked composition bar for whichever level `rootPath` has drilled into.
 * Also returns the plain segment list so the caller can render the DOM
 * legend grid (dc puts the legend as HTML below the bar, not as an ECharts
 * legend component).
 */
export function buildCompositionBar(option: ChartOption, rootPath: string[]): CompositionBarView {
  const [series] = getSeriesList(option);
  const sourceNodes: CompositionNode[] = Array.isArray(series?.data) ? (series.data as CompositionNode[]).filter(isRecord) : [];
  const palette = Array.isArray(option.palette) ? (option.palette as string[]) : DEFAULT_COMPOSITION_PALETTE;

  const rootNode = rootPath.length ? findCompositionNode(sourceNodes, rootPath) : null;
  const children = rootPath.length ? (rootNode ? getCompositionChildren(rootNode) : []) : sourceNodes;
  const sorted = [...children].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
  const total = sorted.reduce((sum, node) => sum + (Number(node.value) || 0), 0) || 1;

  const segments: CompositionBarSegment[] = sorted.map((node, index) => {
    const value = Number(node.value) || 0;
    const name = typeof node.name === "string" ? node.name : "";
    return {
      name,
      value,
      share: (value / total) * 100,
      color: palette[Math.min(index, palette.length - 1)],
      hasChildren: getCompositionChildren(node).length > 0,
      fullPath: [...rootPath, name],
    };
  });

  const barOption: Record<string, unknown> = {
    tooltip: isRecord(option.tooltip)
      ? option.tooltip
      : { trigger: "item", backgroundColor: "#16181A", borderColor: "#16181A", textStyle: { color: "#FFFFFF" } },
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    legend: { show: false },
    xAxis: { type: "value", show: false, max: total },
    yAxis: { type: "category", data: [""], show: false },
    series: segments.map((segment) => ({
      name: segment.name,
      type: "bar",
      stack: "composition",
      barWidth: 26,
      itemStyle: { color: segment.color, borderColor: "#E4E3DC", borderWidth: 1 },
      label: { show: false },
      tooltip: {
        formatter: `${segment.name}<br/>${formatYen(segment.value)}<br/>${segment.share.toFixed(1)}%`,
      },
      data: [
        {
          value: segment.value,
          name: segment.name,
          selectionValue: segment.name,
          hasChildren: segment.hasChildren,
          fullPath: segment.fullPath,
        },
      ],
    })),
  };

  return { segments, option: barOption };
}

function formatYen(value: number): string {
  return `${Math.round(value).toLocaleString("ja-JP")}円`;
}
